import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  DndContext,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useDraggable,
  DragOverlay
} from "@dnd-kit/core";
import { GameShell, useModal } from "./layout";
import Button from "./Button";
import SnackAlert from "./SnackAlert";
import { GameEndSection, LivesDisplay } from "./game";
import { ClozeSkeleton } from "./Skeleton";
import styles from "../styles/Cloze.module.css";
import globalStyles from "../styles/global.module.css";
import modalStyles from "../styles/modal.module.css";
import gameStyles from "../styles/GameStyles.module.css";
import buttonStyles from "../styles/Button.module.css";
import { getGameConfig, getGameMessage } from "../games";
import { logGameEvent } from "../analytics";
import FaIcon from './FaIcon';
import { useClozeEvents } from "../hooks/games/useClozeEvents";
import { hasGameHistory } from "../util";
import AIHelperTrigger from './ai-helper/AIHelperTrigger';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';

const ClozeGame = ({
  date,
  gameConfig: providedGameConfig,
  puzzleConfig,
  sessionState,
  solutions,
  status,
  gameOver,
  won,
  justWon,
  justLost,
  isLoading,
  sessionError,
  isEventPending,
  dispatchEvent,
  isPreview = false,
  previewSponsorship = null,
  showNavigation = true,
  showGameHeader = true,
  showHeaderShareAction = true,
  showStickyAdRail = true,
  showMobileGameplayAd = true,
  simplifyInstructions = false,
  headerActionMode = 'default',
  previewEndLabel,
  showAIHelper = true,
}) => {
  const gameConfig = providedGameConfig || getGameConfig("cloze");
  const effectiveDate = date || "preview";
  const shouldTrack = !isPreview;
  const { getPuzzleInstanceId, getPuzzleInstance } = usePuzzleInstances();
  const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, date);
  const sponsorship = previewSponsorship || (isPreview ? null : getPuzzleInstance(gameConfig.id, effectiveDate)?.sponsorship || null);

  const ERROR_MESSAGES = {
    INVALID_SUBMISSION: "Errore durante l'invio della risposta",
    GAME_LOAD_ERROR: "Errore nel caricamento del gioco",
    SUBMIT_ERROR: "Errore durante l'invio",
  };

  const { isOpen: isModalOpen, openWith: showModal, close: closeModal, title: modalTitle, content: modalContent } = useModal();

  // Cloze-specific event helper
  const { submitPlacements } = useClozeEvents(dispatchEvent);

  // Local UI state (derived from session or for UI interactions)
  const [tokens, setTokens] = useState([]);
  const [placements, setPlacements] = useState([]); // array of placed words by blank index
  const [draggingWord, setDraggingWord] = useState(null);
  const [selectedWord, setSelectedWord] = useState(null); // tap-to-place fallback
  const [submitCooldown, setSubmitCooldown] = useState(false);
  const [feedback, setFeedback] = useState(null); // array of { state } or nulls
  const [showEndContent, setShowEndContent] = useState(false);
  const [lossRevealComplete, setLossRevealComplete] = useState(false);
  const [revealedSolutions, setRevealedSolutions] = useState(null); // Store solutions for static reveal view
  const [endSectionAnchor, setEndSectionAnchor] = useState(null);
  const snackAlertRef = useRef();
  const hasShownInstructionsRef = useRef(false);

  // Derived state from session
  const lives = sessionState?.lives ?? 5;
  const completed = won;

  const sensors = useSensors(
    // Desktop: require a tiny movement to start drag so clicks don't drag
    useSensor(MouseSensor, { activationConstraint: { distance: 3 } }),
    // Mobile: require a slightly longer press to start drag; quick taps remain clicks
    useSensor(TouchSensor, { pressDelay: 250, pressTolerance: 12 })
  );

  const collisionDetection = useCallback((args) => {
    // Use closest-center for collision detection
    const collisions = closestCenter(args);
    if (!collisions || collisions.length === 0) return [];
    // With closestCenter, collisions are already sorted by distance; don't reorder
    return collisions;
  }, []);

  function onDragStart(event) {
    if (completed || gameOver) return;
    const word = event?.active?.data?.current?.word;
    if (word) setDraggingWord(word);
  }

  function onDragEnd(event) {
    const { active, over, delta } = event || {};
    if (!active || completed || gameOver) { setDraggingWord(null); return; }
    const data = active.data?.current || {};
    const word = data.word;
    const fromBlankIndex = data.fromBlankIndex;
    const overId = over?.id;
    if (!word) return;
    const isBlankTarget = (typeof overId === 'string' && overId.startsWith('blank:'));
    const isBankSlot = (typeof overId === 'string' && overId.startsWith('bank:'));
    const isBankContainer = overId === 'bank';
    // If it was a tiny movement (tap) on a placed pill, treat as return to bank
    const isTap = !delta ? false : (Math.abs(delta.x) + Math.abs(delta.y)) < 6;
    // If dropping from a blank and not over another blank (including over=null), OR over bank, OR over a return slot, return to bank
    if (typeof fromBlankIndex === 'number' && (!over || isTap)) {
      handlePlacedClick(fromBlankIndex);
      setDraggingWord(null);
      return;
    }
    if (isBlankTarget) {
      const idx = parseInt(String(overId).split(':')[1], 10);
      if (!Number.isNaN(idx)) {
        handleDropOnBlank(idx, word, fromBlankIndex);
        return;
      }
    }
    if (isBankSlot) {
      const idx = parseInt(String(overId).split(':')[1], 10);
      if (!Number.isNaN(idx)) {
        if (typeof fromBlankIndex === 'number') {
          // Remove the word from its blank and clear feedback
          setPlacements(prev => {
            const next = [...prev];
            if (next[fromBlankIndex] === word) next[fromBlankIndex] = null;
            return next;
          });
          setFeedback(prev => {
            if (!prev) return prev;
            const copy = [...prev];
            if (fromBlankIndex >= 0 && fromBlankIndex < copy.length) copy[fromBlankIndex] = null;
            return copy;
          });
          setSelectedWord(null);
        }
        setDraggingWord(null);
        return;
      }
    }
    if (isBankContainer) {
      if (typeof fromBlankIndex === 'number') {
        // Same behavior as clicking to return
        handlePlacedClick(fromBlankIndex);
      }
      setDraggingWord(null);
      return;
    }
    setDraggingWord(null);
  }

  // Initialize UI from session state and puzzle config
  useEffect(() => {
    if (isLoading || !puzzleConfig) return;

    // Parse text into tokens
    const tks = parseClozeText(puzzleConfig.text || "");
    setTokens(tks);
    const blankCount = tks.filter(t => t.type === 'blank').length;

    // Initialize placements from session state or empty
    const sessionPlacements = sessionState?.placements || [];
    setPlacements(Array.from({ length: blankCount }, (_, i) => sessionPlacements[i] ?? null));

    // Initialize feedback from session state
    const sessionFeedback = sessionState?.feedback || null;
    if (sessionFeedback) {
      setFeedback(Array.from({ length: blankCount }, (_, i) => sessionFeedback[i] ?? null));
    } else {
      setFeedback(null);
    }

    // Show end content if game is already over
    if (gameOver && !justLost) {
      setShowEndContent(true);
      // For reloaded lost games, show the correct solutions (without animation)
      // The animation is only applied when justLost is true (real-time loss)
      if (solutions && solutions.length > 0 && !won) {
        setRevealedSolutions(solutions.map(s => s.word));
      }
    }

    if (!isPreview) {
      // Show instructions for first-time players (only once)
      if (!hasShownInstructionsRef.current && !hasGameHistory(gameConfig.id)) {
        hasShownInstructionsRef.current = true;
        showModal({
          title: "Istruzioni",
          content: <Instructions simplified={simplifyInstructions} onDismiss={closeModal} />
        });
        if (shouldTrack) {
          logGameEvent('instructions_viewed', gameConfig.id, effectiveDate, { first_time: true });
        }
      }
    }
  }, [
    isLoading,
    puzzleConfig,
    sessionState,
    gameOver,
    gameConfig.id,
    effectiveDate,
    showModal,
    solutions,
    won,
    justLost,
    isPreview,
    shouldTrack
  ]);

  // Sync placements from session state when it changes (e.g., after submit)
  useEffect(() => {
    if (!sessionState?.placements) return;
    const blankCount = tokens.filter(t => t.type === 'blank').length;
    if (blankCount === 0) return;
    
    setPlacements(prev => {
      const sessionPlacements = sessionState.placements;
      // Only update if different
      const hasChanges = prev.some((p, i) => p !== sessionPlacements[i]);
      if (!hasChanges) return prev;
      return Array.from({ length: blankCount }, (_, i) => sessionPlacements[i] ?? null);
    });
  }, [sessionState?.placements, tokens]);

  // Sync feedback from session state
  useEffect(() => {
    if (sessionState?.feedback) {
      setFeedback(sessionState.feedback);
    }
  }, [sessionState?.feedback]);

  const availableOptions = (puzzleConfig?.bank || []).filter(opt => !placements.includes(opt));
  const bankSlots = (puzzleConfig?.bank || []).map((opt, i) => ({ id: `bank:${i}`, word: opt }));
  const isShowingTargets = Boolean(draggingWord || selectedWord);
  const isAllPlaced = placements.length > 0 && placements.every(Boolean);

  function parseClozeText(text) {
    const parts = text.split(/___/g);
    const out = [];
    let blankIndex = 0;
    parts.forEach((part, idx) => {
      if (part) out.push({ type: 'text', value: part });
      if (idx < parts.length - 1) {
        out.push({ type: 'blank', index: blankIndex++ });
      }
    });
    return out;
  }

  // DnD Kit helpers
  function DraggablePill({ word, disabled, selected, onClick, id }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: id || `pill:${word}`, data: { word } });
    const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, cursor: 'grabbing' } : { cursor: 'grab' };
    return (
      <button
        ref={setNodeRef}
        type="button"
        className={`${styles.wordPill} ${selected ? styles.wordPillSelected : ''}`}
        {...attributes}
        {...listeners}
        style={style}
        onClick={onClick}
        disabled={disabled}
      >
        {word}
      </button>
    );
  }

  function DraggablePlaced({ word, index, disabled, className, onClick }) {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: `placed:${index}`, data: { word, fromBlankIndex: index } });
    const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, cursor: 'grabbing' } : { cursor: 'grab' };
    return (
      <button
        ref={setNodeRef}
        type="button"
        className={className}
        {...attributes}
        {...listeners}
        style={style}
        onClick={onClick}
        disabled={disabled}
      >
        {word}
      </button>
    );
  }

  function BlankSlot({ index, active, onClick, occupied, children }) {
    const { isOver, setNodeRef } = useDroppable({ id: `blank:${index}` });
    if (occupied) {
      return (
        <span ref={setNodeRef}>
          {children}
        </span>
      );
    }
    const cls = [styles.inlinePill, styles.inlineTarget, (active || isOver) ? styles.inlineTargetActive : ''].filter(Boolean).join(' ');
    return <span ref={setNodeRef} className={cls} onClick={onClick} />;
  }

  function BankSlot({ slotId, word }) {
    const { isOver, setNodeRef } = useDroppable({ id: slotId });
    const active = Boolean(draggingWord);
    const isPlacedInText = placements.includes(word);
    const cls = [styles.inlinePill, styles.inlineTarget, (active || isOver) ? styles.inlineTargetActive : ''].filter(Boolean).join(' ');
    if (isPlacedInText) {
      return <span ref={setNodeRef} className={cls} />;
    }
    return (
      <DraggablePill id={`pill:${word}`} word={word} disabled={false} selected={selectedWord === word} onClick={() => handlePillClick(word)} />
    );
  }

  function BankDropZone({ children, disabled }) {
    const { isOver, setNodeRef } = useDroppable({ id: 'bank' });
    return (
      <div ref={setNodeRef} className={styles.wordBank + (isOver ? ' drag-over' : '')} aria-disabled={disabled} aria-current={isOver ? 'over' : undefined} aria-dropeffect={!disabled ? 'move' : undefined}>
        {children}
      </div>
    );
  }

  function handlePillClick(word) {
    if (completed || gameOver) return;
    setSelectedWord(prev => prev === word ? null : word);
  }

  function placeWordAt(index, word) {
    if (completed || gameOver) return;
    if (!word) return;
    setPlacements(prev => {
      const next = [...prev];
      // If this word is already placed elsewhere, clear it first
      const existingIdx = next.findIndex(w => w === word);
      if (existingIdx !== -1) next[existingIdx] = null;
      next[index] = word;
      return next;
    });
    setFeedback(prev => {
      if (!prev) return prev;
      const copy = [...prev];
      if (index >= 0 && index < copy.length) copy[index] = null;
      return copy;
    });
    setDraggingWord(null);
    setSelectedWord(null);
  }

  function handleDropOnBlank(targetIndex, draggedWord, fromBlankIndex) {
    if (completed || gameOver) return;
    if (typeof targetIndex !== 'number' || !draggedWord) return;
    setPlacements(prev => {
      const next = [...prev];
      const targetWord = next[targetIndex] || null;
      if (typeof fromBlankIndex === 'number') {
        if (fromBlankIndex === targetIndex) {
          return next;
        }
        // swap if occupied, else move
        if (targetWord) {
          next[targetIndex] = draggedWord;
          next[fromBlankIndex] = targetWord;
        } else {
          next[targetIndex] = draggedWord;
          next[fromBlankIndex] = null;
        }
      } else {
        // from bank: replace or fill
        const existingIdx = next.findIndex(w => w === draggedWord);
        if (existingIdx !== -1) next[existingIdx] = null;
        next[targetIndex] = draggedWord;
      }
      return next;
    });
    setFeedback(prev => {
      if (!prev) return prev;
      const copy = [...prev];
      if (targetIndex >= 0 && targetIndex < copy.length) copy[targetIndex] = null;
      if (typeof fromBlankIndex === 'number' && fromBlankIndex >= 0 && fromBlankIndex < copy.length) copy[fromBlankIndex] = null;
      return copy;
    });
    setDraggingWord(null);
    setSelectedWord(null);
  }

  function handlePlacedClick(index, evt) {
    if (evt && typeof evt.stopPropagation === 'function') {
      evt.stopPropagation();
      evt.preventDefault();
    }
    setPlacements(prev => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    setFeedback(prev => {
      if (!prev) return prev;
      const copy = [...prev];
      if (index >= 0 && index < copy.length) copy[index] = null;
      return copy;
    });
    // Clear any selected word to avoid immediate re-placement into another blank
    setSelectedWord(null);
  }

  function handleReset() {
    if (completed || gameOver) return;
    const blankCount = tokens.filter(t => t.type === 'blank').length;
    setPlacements(Array.from({ length: blankCount }, () => null));
    setFeedback(prev => (prev ? Array.from({ length: blankCount }, () => null) : null));
    setSelectedWord(null);
    setDraggingWord(null);
  }

  async function handleSubmit() {
    if (!isAllPlaced || isEventPending || submitCooldown) return;
    setSubmitCooldown(true);
    
    try {
      const result = await submitPlacements(placements);
      
      if (result.success) {
        const { complete, feedback: newFeedback, lives_remaining, solutions } = result.result || {};
        
        // Update feedback
        if (Array.isArray(newFeedback)) {
          setFeedback(newFeedback);
        }
        
        if (complete) {
          // Win
          snackAlertRef.current?.showSnackAlert("Tutto corretto!", "success", 2000);
          if (shouldTrack) {
            logGameEvent('guess_correct', gameConfig.id, effectiveDate);
          }
          setShowEndContent(true);
        } else {
          // Incorrect submission
          if (shouldTrack) {
            logGameEvent('guess_incorrect', gameConfig.id, effectiveDate);
          }
          const isLastLife = lives_remaining === 0;
          
          if (isLastLife) {
            // Game over - show solutions reveal animation
            await handleGameOverLoss(solutions);
          } else {
            const message = "Ci sono errori, ritenta";
            snackAlertRef.current?.showSnackAlert(message, "error", 2000);
          }
        }
      } else {
        // Handle error
        const errorMessage = result.message || ERROR_MESSAGES.SUBMIT_ERROR;
        snackAlertRef.current?.showSnackAlert(errorMessage, "error", 1500);
      }
    } catch (err) {
      snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.SUBMIT_ERROR, "error", 1500);
    } finally {
      setTimeout(() => setSubmitCooldown(false), 1500);
    }
  }

  async function handleGameOverLoss(solutions) {
    setSelectedWord(null);
    setDraggingWord(null);
    
    // Store solutions in a separate state for static rendering
    // This view won't be affected by other state updates
    if (solutions && solutions.length > 0) {
      // solutions comes as array of {word: string} objects, extract just the words
      setRevealedSolutions(solutions.map(s => s.word));
      
      // Calculate total animation time: (count * 900ms delay) + 800ms animation duration
      const totalAnimationTime = (solutions.length * 900) + 800;
      
      // Wait for all animations to complete
      await new Promise(resolve => setTimeout(resolve, totalAnimationTime));
    }
    
    // Show end content inline
    setShowEndContent(true);
    setLossRevealComplete(true);
  }

  const handleClosePreview = useCallback(() => {
    window.close();
  }, []);

  const mobileGameplayAd = {
    enabled: !!endSectionAnchor,
    anchors: endSectionAnchor ? [endSectionAnchor] : [],
    placement: `game-shell-${gameConfig.id}-mobile-gameplay-banner`,
    testId: `${gameConfig.id}-mobile-gameplay-banner`,
  };

  // Build header actions
  const fullHeaderActions = [
    {
      icon: "fa fa-question-circle",
      title: "Istruzioni",
      onClick: () => {
        if (shouldTrack) {
          logGameEvent("instructions_viewed", gameConfig.id, effectiveDate, { first_time: false });
        }
        showModal({
          title: "Istruzioni",
          content: <Instructions simplified={simplifyInstructions} onDismiss={closeModal} />
        });
      },
    },
  ];
  const headerActions = headerActionMode === 'instructions-only'
    ? fullHeaderActions.slice(0, 1)
    : fullHeaderActions;

  // Show skeleton while loading
  if (isLoading || !puzzleConfig) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
        showHeaderShareAction={showHeaderShareAction}
        showStickyAdRail={showStickyAdRail}
      >
        <div className={styles.container}>
          <p className={gameStyles.greeting}>
            {getGameMessage(gameConfig.id, "not_started")}
          </p>
          <ClozeSkeleton />
        </div>
      </GameShell>
    );
  }

  // Show error state
  if (sessionError) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
        showHeaderShareAction={showHeaderShareAction}
        showStickyAdRail={showStickyAdRail}
      >
        <div className={styles.container}>
          <p className={globalStyles.encouragement}>
            {ERROR_MESSAGES.GAME_LOAD_ERROR}
          </p>
        </div>
      </GameShell>
    );
  }

  const aiSelectionEnabled = gameOver && !isPreview;
  const isAIOnboardingReady = gameOver && showEndContent && (!justLost || lossRevealComplete) && !isPreview;

  return (
    <GameShell
      gameConfig={gameConfig}
      actions={headerActions}
      sponsorship={sponsorship}
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle={modalTitle}
      modalContent={modalContent}
      key={sessionState?.session_id || effectiveDate}
      resultsPresentationReady={showEndContent && (!justLost || lossRevealComplete)}
      showCelebration={justWon}
      isCompleted={gameOver}
      showNavigation={showNavigation}
      showGameHeader={showGameHeader}
      showHeaderShareAction={showHeaderShareAction}
      showStickyAdRail={showStickyAdRail}
      mobileGameplayAd={showMobileGameplayAd ? mobileGameplayAd : null}
    >
      <div className={styles.container}>
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <DragOverlay zIndex={2000} dropAnimation={{duration: 200}}>
            {draggingWord ? (
              <button type="button" className={styles.wordPill}>
                {draggingWord}
              </button>
            ) : null}
          </DragOverlay>
          <SnackAlert ref={snackAlertRef} />
          {!gameOver && (
            <p className={gameStyles.greeting}>
              {getGameMessage(gameConfig.id, "not_started")}
            </p>
          )}
          {puzzleConfig && (
            <div className={`${styles.placeholderCard} ${(completed || gameOver) ? styles.noBottomInset : ''}`}>
              {showAIHelper ? (
                <AIHelperTrigger
                  puzzleInstanceId={puzzleInstanceId}
                  gameSlug="cloze"
                  enabled={aiSelectionEnabled}
                  onboardingReady={isAIOnboardingReady}
                  coachmarkSelector='[data-ai-component="solution_word"]'
                >
                  <h2 className={styles.placeholderTitle}><span data-ai-component="title">{puzzleConfig.title || gameConfig.displayName}</span></h2>
                  <p className={styles.gameText}>
                    {/* Separate view for lost games showing correct solutions */}
                    {revealedSolutions ? (
                      tokens.map((t, i) => {
                        if (t.type === 'text') return <span key={i}>{t.value}</span>;
                        const word = revealedSolutions[t.index];
                        // Only apply reveal animation when player just lost (not when reloading)
                        const cls = justLost
                          ? `${styles.inlinePill} ${styles.inlinePlaced} ${styles.inlineCorrect} ${styles.inlineReveal}`
                          : `${styles.inlinePill} ${styles.inlinePlaced} ${styles.inlineCorrect}`;
                        return (
                          <span
                            key={i}
                            className={cls}
                            style={justLost ? { animationDelay: `${t.index * 900}ms` } : undefined}
                          >
                            <span data-ai-component="solution_word" data-ai-word={word}>{word}</span>
                          </span>
                        );
                      })
                    ) : (
                      /* Regular interactive/completed view */
                      tokens.map((t, i) => {
                        if (t.type === 'text') return <span key={i}>{t.value}</span>;
                        const placed = placements[t.index];
                        if (placed) {
                          const cls = [
                            styles.inlinePill,
                            styles.inlinePlaced,
                            (feedback && feedback[t.index]?.state === 'correct') ? styles.inlineCorrect : '',
                            (feedback && feedback[t.index]?.state === 'incorrect') ? styles.inlineIncorrect : '',
                          ].filter(Boolean).join(' ');
                          return (
                            <BlankSlot key={i} index={t.index} active={isShowingTargets} occupied>
                              {(completed || gameOver) ? (
                                <span className={cls}>
                                  <span data-ai-component="solution_word" data-ai-word={placed}>{placed}</span>
                                </span>
                              ) : (
                                <DraggablePlaced
                                  word={placed}
                                  index={t.index}
                                  disabled={completed || gameOver}
                                  className={cls}
                                  onClick={(e) => handlePlacedClick(t.index, e)}
                                />
                              )}
                            </BlankSlot>
                          );
                        }
                        return (
                          <BlankSlot
                            key={i}
                            index={t.index}
                            active={isShowingTargets}
                            onClick={(!completed && !gameOver) ? () => selectedWord && placeWordAt(t.index, selectedWord) : undefined}
                          />
                        );
                      })
                    )}
                  </p>
                </AIHelperTrigger>
              ) : (
                <>
                  <h2 className={styles.placeholderTitle}><span>{puzzleConfig.title || gameConfig.displayName}</span></h2>
                  <p className={styles.gameText}>
                    {revealedSolutions ? (
                      tokens.map((t, i) => {
                        if (t.type === 'text') return <span key={i}>{t.value}</span>;
                        const word = revealedSolutions[t.index];
                        const cls = justLost
                          ? `${styles.inlinePill} ${styles.inlinePlaced} ${styles.inlineCorrect} ${styles.inlineReveal}`
                          : `${styles.inlinePill} ${styles.inlinePlaced} ${styles.inlineCorrect}`;
                        return (
                          <span
                            key={i}
                            className={cls}
                            style={justLost ? { animationDelay: `${t.index * 900}ms` } : undefined}
                          >
                            {word}
                          </span>
                        );
                      })
                    ) : (
                      tokens.map((t, i) => {
                        if (t.type === 'text') return <span key={i}>{t.value}</span>;
                        const placed = placements[t.index];
                        if (placed) {
                          const cls = [
                            styles.inlinePill,
                            styles.inlinePlaced,
                            (feedback && feedback[t.index]?.state === 'correct') ? styles.inlineCorrect : '',
                            (feedback && feedback[t.index]?.state === 'incorrect') ? styles.inlineIncorrect : '',
                          ].filter(Boolean).join(' ');
                          return (
                            <BlankSlot key={i} index={t.index} active={isShowingTargets} occupied>
                              {(completed || gameOver) ? (
                                <span className={cls}>{placed}</span>
                              ) : (
                                <DraggablePlaced
                                  word={placed}
                                  index={t.index}
                                  disabled={completed || gameOver}
                                  className={cls}
                                  onClick={(e) => handlePlacedClick(t.index, e)}
                                />
                              )}
                            </BlankSlot>
                          );
                        }
                        return (
                          <BlankSlot
                            key={i}
                            index={t.index}
                            active={isShowingTargets}
                            onClick={(!completed && !gameOver) ? () => selectedWord && placeWordAt(t.index, selectedWord) : undefined}
                          />
                        );
                      })
                    )}
                  </p>
                </>
              )}
            </div>
          )}

          {/* End game section - shows inline when game ends */}
          {gameOver && showEndContent && (!justLost || lossRevealComplete) && (
            isPreview && !previewSponsorship ? (
              <div ref={setEndSectionAnchor} className={styles.previewEndSection}>
                <Button primary onClick={handleClosePreview}>
                  {previewEndLabel || 'Chiudi anteprima'}
                </Button>
              </div>
            ) : (
              <GameEndSection
                ref={setEndSectionAnchor}
                won={completed}
                justWon={justWon}
                gameConfig={gameConfig}
                sessionState={sessionState}
                puzzleConfig={puzzleConfig}
                justCompleted={justWon || justLost}
                date={effectiveDate}
                shouldTrack={shouldTrack}
                sponsorship={sponsorship}
                previewMode={isPreview}
                message={completed ? 'Hai completato il testo! 🎉' : 'Peccato! Riprova domani. 💪'}
                snackAlertRef={snackAlertRef}
              />
            )
          )}

          {/* Bottom panel with controls - only shown during active gameplay */}
          {puzzleConfig && !completed && !gameOver && (
            <div className={styles.bottomPanel}>
              <LivesDisplay lives={lives} maxLives={5} />
              {!isAllPlaced && (
                  <BankDropZone disabled={completed || gameOver}>
                  {bankSlots.map(({ id, word }) => (
                      <BankSlot key={id} slotId={id} word={word} />
                  ))}
                  </BankDropZone>
              )}
              {isAllPlaced && availableOptions.length === 0 && !gameOver && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 12 }}>
                  <>
                    <Button className={"secondary"} onClick={handleReset} disabled={isEventPending}>
                      Reimposta
                    </Button>
                    <Button 
                      primary 
                      onClick={handleSubmit} 
                      disabled={isEventPending || submitCooldown}
                      loading={isEventPending}
                      loadingText="Invio"
                    >
                      Invia
                    </Button>
                  </>
                </div>
              )}
            </div>
          )}
        </DndContext>
      </div>
    </GameShell>
  );
};

export default ClozeGame;

// Mini demo: due spazi vuoti, posizionamento uno per volta, feedback rosso/verde, reset e pausa
const MiniClozeDemo = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    // 0: empty-empty
    // 1: place wrong in blank1
    // 2: place wrong in blank2
    // 3: show wrong feedback (both red)
    // 4: clear both blanks
    // 5: place correct in blank1
    // 6: place correct in blank2
    // 7: show correct feedback (both green)
    // 8: pause 2s, then restart
    const durations = [800, 450, 450, 900, 600, 450, 450, 900, 2000];
    const timer = setTimeout(() => setPhase((p) => (p + 1) % 9), durations[phase] || 900);
    return () => clearTimeout(timer);
  }, [phase]);

  const word1 = "c'è"; // for blank1 (Oggi ___ il sole.)
  const word2 = "fa";   // for blank2 (Domani ___ freddo.)

  // Compute placement and status per blank based on phase
  let b1Word = null, b2Word = null;
  let b1Status = 'empty', b2Status = 'empty'; // empty | placed | incorrect | correct
  if (phase === 1) { // place wrong first
    b1Word = word2; b1Status = 'placed';
  } else if (phase === 2) {
    b1Word = word2; b1Status = 'placed';
    b2Word = word1; b2Status = 'placed';
  } else if (phase === 3) { // show wrong feedback
    b1Word = word2; b1Status = 'incorrect';
    b2Word = word1; b2Status = 'incorrect';
  } else if (phase === 5) { // place correct first
    b1Word = word1; b1Status = 'placed';
  } else if (phase === 6) {
    b1Word = word1; b1Status = 'placed';
    b2Word = word2; b2Status = 'placed';
  } else if (phase === 7 || phase === 8) { // show correct feedback (and keep during pause)
    b1Word = word1; b1Status = 'correct';
    b2Word = word2; b2Status = 'correct';
  }

  const blankClass = (status) => {
    const base = [styles.inlinePill];
    if (status === 'empty') {
      base.push(styles.inlineTarget, styles.inlineTargetActive);
    } else {
      base.push(styles.inlinePlaced);
      if (status === 'incorrect') base.push(styles.inlineIncorrect);
      if (status === 'correct') base.push(styles.inlineCorrect);
    }
    return base.filter(Boolean).join(' ');
  };

  const bankOpacity1 = (b1Word === word1 || b2Word === word1) ? 0.5 : 1;
  const bankOpacity2 = (b1Word === word2 || b2Word === word2) ? 0.5 : 1;

  return (
    <div style={{ margin: '10px 0 16px 0' }}>
      <p className={styles.gameText} style={{ textAlign: 'center' }}>
        Oggi <span className={blankClass(b1Status)}>{b1Word || ''}</span> il sole. Domani <span className={blankClass(b2Status)}>{b2Word || ''}</span> freddo.
      </p>
      <div className={styles.wordBank} style={{ pointerEvents: 'none', paddingTop: 6 }}>
        <span className={styles.wordPill} style={{ opacity: bankOpacity1 }}>{word1}</span>
        <span className={styles.wordPill} style={{ opacity: bankOpacity2 }}>{word2}</span>
      </div>
    </div>
  );
};

const Instructions = ({ simplified = false, onDismiss = null }) => {
  if (simplified) return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Completa il testo inserendo la parola giusta in ogni spazio vuoto.</p>
      <MiniClozeDemo />
      <Button
        primary
        onClick={onDismiss}
        className={`${buttonStyles.primaryBlue} ${buttonStyles.inline}`}
      >
        Gioca
        <FaIcon className={`fa-solid fa-play ${styles.instructionsCtaIcon}`} />
      </Button>
    </div>
  );
  return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Completa il testo inserendo la parola giusta in ogni spazio vuoto.</p>
      <MiniClozeDemo />
      <ul>
        <li>Per inserire una parola, trascinala in uno spazio vuoto oppure toccala e poi tocca lo spazio vuoto.</li>
        <li>Per rimuovere una parola, cliccaci sopra o trascinala in basso tra le parole della lista.</li>
        <li>Riempi tutti gli spazi e premi <strong>Invia</strong> per controllare.</li>
        <li>Le parole corrette diventano verdi; quelle sbagliate rosse.</li>
        <li>Se sbagli, perdi una vita. <strong>Hai 5 vite</strong> a disposizione per completare il gioco!</li>
      </ul>
    </div>
  );
};
